import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ActiveTimer, TimerStatus, TaskStatus } from '@/types';
import { timeService, taskService } from '@/services/api';
import { queryClient, QUERY_KEYS } from '@/services/queryClient';

// Maximum concurrent timers allowed
export const MAX_CONCURRENT_TIMERS = 2;

// LocalStorage key for tracking last running task
const STOPPED_TASKS_KEY = 'nct_stopped_tasks';

export interface TimerState {
  activeTimers: ActiveTimer[]; // Support multiple timers (max 2)
  // Legacy support - keeping for backward compatibility
  activeTimer: ActiveTimer | null;
  // Timer interval reference (stored outside Redux for performance)
  isRunning: boolean;
  currentTimeLogId?: string;
  // Dialog state for when user tries to start 3rd timer
  pendingTimerTask?: { taskId: string; userId: string; taskTitle: string };
  showStopTimerDialog: boolean;
  /** User-facing error from the most recent failed timer start attempt. */
  timerStartError: string | null;
}

const initialState: TimerState = {
  activeTimers: [],
  activeTimer: null,
  isRunning: false,
  currentTimeLogId: undefined,
  pendingTimerTask: undefined,
  showStopTimerDialog: false,
  timerStartError: null,
};

// Async thunk to start timer and create time log entry
export const startTimerAsync = createAsyncThunk(
  'timer/startTimer',
  async ({ taskId, userId, taskTitle }: { taskId: string; userId: string; taskTitle: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { timer: TimerState };
      
      // Check if already timing this task
      if (state.timer.activeTimers.some(t => t.taskId === taskId)) {
        return rejectWithValue({ message: 'Timer already running for this task' });
      }
      
      // Check if max timers reached (2)
      if (state.timer.activeTimers.length >= MAX_CONCURRENT_TIMERS) {
        return rejectWithValue({ 
          message: 'Maximum concurrent timers reached',
          code: 'MAX_TIMERS_REACHED',
          pendingTask: { taskId, userId, taskTitle }
        });
      }
      
      const response = await timeService.startTimer(taskId);
      return {
        taskId,
        userId,
        taskTitle,
        activeTimer: response,
      };
    } catch (error) {
      const msg =
        (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ?? (error as Error)?.message ?? 'Failed to start timer';
      return rejectWithValue({ message: msg });
    }
  }
);

// Async thunk to stop timer and update time log entry
export const stopTimerAsync = createAsyncThunk(
  'timer/stopTimer',
  async ({ taskId, reason }: { taskId?: string; reason?: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { timer: TimerState; tasks?: { items?: Array<{ id: string; status: TaskStatus }> } };
      
      // If taskId not provided, use the first timer (legacy behavior)
      const targetTaskId = taskId || state.timer.activeTimers[0]?.taskId;
      
      if (!targetTaskId) {
        throw new Error('No active timer found');
      }

      const response = await timeService.stopTimer(targetTaskId, reason);

      const currentTask = state.tasks?.items?.find((t) => t.id === targetTaskId);
      if (currentTask &&
        currentTask.status !== TaskStatus.COMPLETED &&
        currentTask.status !== TaskStatus.CANCELLED &&
        currentTask.status !== TaskStatus.IN_PROGRESS
      ) {
        await taskService.updateTaskStatus(targetTaskId, TaskStatus.IN_PROGRESS);
      }
      
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.taskTimeLogs(targetTaskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timeLogs });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(targetTaskId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });

      return {
        taskId: targetTaskId,
        timeLog: response,
      };
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ?? (error as Error)?.message ?? '';
      return rejectWithValue({ message: msg });
    }
  }
);

// Async thunk to pause timer (persists to backend)
export const pauseTimerAsync = createAsyncThunk(
  'timer/pauseTimer',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const activeTimer = await timeService.pauseTimer(taskId);
      return activeTimer;
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ?? (error as Error)?.message ?? '';
      return rejectWithValue({ message: msg });
    }
  }
);

// Async thunk to resume timer (persists to backend)
export const resumeTimerAsync = createAsyncThunk(
  'timer/resumeTimer',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const activeTimer = await timeService.resumeTimer(taskId);
      return activeTimer;
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ?? (error as Error)?.message ?? '';
      return rejectWithValue({ message: msg });
    }
  }
);

/**
 * Async thunk to pause all active running timers
 * 
 * Use case: Called before user logout to ensure timers are paused and persisted to backend.
 * This prevents losing timer state and allows users to resume timers after logging back in.
 * 
 * Flow:
 * 1. Gets all active timers from Redux state
 * 2. Filters to only running timers (paused timers are already saved)
 * 3. Calls backend pause API for each running timer in parallel
 * 4. Returns array of paused timer objects from backend
 * 
 * @returns {Promise<ActiveTimer[]>} Array of paused timer objects with updated status
 * @throws {Error} If pause operation fails for any timer
 */
export const pauseAllTimersAsync = createAsyncThunk(
  'timer/pauseAllTimers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { timer: TimerState };
      // Filter for only running timers (status === 'running')
      const runningTimers = state.timer.activeTimers.filter(t => t.status === TimerStatus.RUNNING);
      
      // Pause each running timer via backend API (parallel execution)
      const pausePromises = runningTimers.map(timer => 
        timeService.pauseTimer(timer.taskId)
      );
      
      // Wait for all pause operations to complete
      const pausedTimers = await Promise.all(pausePromises);
      return pausedTimers;
    } catch (error) {
      const msg = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ?? (error as Error)?.message ?? '';
      return rejectWithValue({ message: msg });
    }
  }
);

const timerSlice = createSlice({
  name: 'timer',
  initialState,
  reducers: {
    // Synchronous action for immediate UI updates (legacy support)
    startTimer: (
      state,
      action: PayloadAction<{ taskId: string; userId: string; taskTitle: string }>
    ) => {
      // Check if already timing this task
      if (state.activeTimers.some(t => t.taskId === action.payload.taskId)) {
        return;
      }
      
      // Check if max timers reached
      if (state.activeTimers.length >= MAX_CONCURRENT_TIMERS) {
        state.pendingTimerTask = action.payload;
        state.showStopTimerDialog = true;
        return;
      }
      
      const newTimer: ActiveTimer = {
        taskId: action.payload.taskId,
        taskTitle: action.payload.taskTitle,
        userId: action.payload.userId,
        startTime: new Date().toISOString(),
        status: TimerStatus.RUNNING,
        accumulatedTime: 0,
      };
      
      state.activeTimers.push(newTimer);
      // Legacy support - keep first timer as activeTimer
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
    },
    pauseTimer: (state, action: PayloadAction<string | undefined>) => {
      const taskId = action.payload;
      if (taskId) {
        // Pause specific timer
        const timer = state.activeTimers.find(t => t.taskId === taskId);
        if (timer) {
          timer.status = TimerStatus.PAUSED;
          timer.pausedAt = new Date().toISOString();
        }
      } else {
        // Pause all timers (legacy behavior)
        state.activeTimers.forEach(timer => {
          timer.status = TimerStatus.PAUSED;
          timer.pausedAt = new Date().toISOString();
        });
      }
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
    },
    resumeTimer: (state, action: PayloadAction<string | undefined>) => {
      const taskId = action.payload;
      if (taskId) {
        // Resume specific timer
        const timer = state.activeTimers.find(t => t.taskId === taskId);
        if (timer) {
          timer.status = TimerStatus.RUNNING;
          timer.startTime = new Date().toISOString();
          timer.pausedAt = undefined;
        }
      } else {
        // Resume all paused timers (legacy behavior)
        state.activeTimers.forEach(timer => {
          if (timer.status === TimerStatus.PAUSED) {
            timer.status = TimerStatus.RUNNING;
            timer.startTime = new Date().toISOString();
            timer.pausedAt = undefined;
          }
        });
      }
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
    },
    // Synchronous action for immediate UI updates (legacy support)
    stopTimer: (state, action: PayloadAction<string | undefined>) => {
      const taskId = action.payload;
      if (taskId) {
        // Stop specific timer
        state.activeTimers = state.activeTimers.filter(t => t.taskId !== taskId);
      } else {
        // Stop all timers (legacy behavior)
        state.activeTimers = [];
      }
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
      state.currentTimeLogId = undefined;
    },
    tick: (state) => {
      state.activeTimers.forEach((timer) => {
        if (timer.status === TimerStatus.RUNNING) {
          timer.accumulatedTime += 1;
        }
      });
      state.activeTimer = state.activeTimers[0] || null;
    },
    setAccumulatedTime: (state, action: PayloadAction<{ taskId?: string; time: number }>) => {
      const { taskId, time } = action.payload;
      if (taskId) {
        const timer = state.activeTimers.find(t => t.taskId === taskId);
        if (timer) {
          timer.accumulatedTime = time;
        }
      } else if (state.activeTimer) {
        state.activeTimer.accumulatedTime = time;
      }
    },
    restoreTimer: (state, action: PayloadAction<ActiveTimer>) => {
      // Add to activeTimers if not already present
      if (!state.activeTimers.some(t => t.taskId === action.payload.taskId)) {
        if (state.activeTimers.length < MAX_CONCURRENT_TIMERS) {
          state.activeTimers.push(action.payload);
        }
      }
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
    },
    // Dialog actions
    setPendingTimerTask: (state, action: PayloadAction<{ taskId: string; userId: string; taskTitle: string } | undefined>) => {
      state.pendingTimerTask = action.payload;
      state.showStopTimerDialog = !!action.payload;
    },
    closeStopTimerDialog: (state) => {
      state.showStopTimerDialog = false;
      state.pendingTimerTask = undefined;
    },
    clearTimerStartError: (state) => {
      state.timerStartError = null;
    },
    // Stop a specific timer and start the pending one
    stopAndStartPending: (state, action: PayloadAction<string | undefined>) => {
      const taskIdToStop = action.payload;

      if (state.pendingTimerTask) {
        // Remove the specified timer (or first one if not specified)
        if (taskIdToStop) {
          state.activeTimers = state.activeTimers.filter(t => t.taskId !== taskIdToStop);
        } else if (state.activeTimers.length > 0) {
          state.activeTimers.shift();
        }

        // Add the pending task as a new timer
        const newTimer: ActiveTimer = {
          taskId: state.pendingTimerTask.taskId,
          taskTitle: state.pendingTimerTask.taskTitle,
          userId: state.pendingTimerTask.userId,
          startTime: new Date().toISOString(),
          status: TimerStatus.RUNNING,
          accumulatedTime: 0,
        };
        state.activeTimers.push(newTimer);
      }
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
      state.showStopTimerDialog = false;
      state.pendingTimerTask = undefined;
    },
    /** Removes the active timer for the given task (e.g. when task is marked completed). */
    clearTimerForTask: (state, action: PayloadAction<string>) => {
      const taskId = action.payload;
      state.activeTimers = state.activeTimers.filter(t => t.taskId !== taskId);
      state.activeTimer = state.activeTimers[0] || null;
      state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle startTimerAsync
      .addCase(startTimerAsync.fulfilled, (state, action) => {
        state.timerStartError = null;
        const serverTimer = action.payload.activeTimer;
        const newTimer: ActiveTimer = {
          ...serverTimer,
          taskId: action.payload.taskId,
          taskTitle: action.payload.taskTitle,
          userId: action.payload.userId,
        };

        if (!state.activeTimers.some((t) => t.taskId === action.payload.taskId)) {
          state.activeTimers.push(newTimer);
        }

        state.activeTimer = state.activeTimers[0] || null;
        state.isRunning = state.activeTimers.some((t) => t.status === TimerStatus.RUNNING);

        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(action.payload.taskId) });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });

        // Remove this task from stopped tasks list when starting
        try {
          const stored = localStorage.getItem(STOPPED_TASKS_KEY);
          if (stored) {
            const stoppedTasks = JSON.parse(stored);
            const updatedStoppedTasks = stoppedTasks.filter(
              (t: { taskId: string }) => t.taskId !== action.payload.taskId
            );
            localStorage.setItem(STOPPED_TASKS_KEY, JSON.stringify(updatedStoppedTasks));
          }
        } catch (error) {
          console.error('Failed to update stopped tasks:', error);
        }
      })
      .addCase(startTimerAsync.rejected, (state, action) => {
        const payload = action.payload as {
          code?: string;
          message?: string;
          pendingTask?: { taskId: string; userId: string; taskTitle: string };
        } | undefined;
        if (payload?.code === 'MAX_TIMERS_REACHED' && payload?.pendingTask) {
          state.pendingTimerTask = payload.pendingTask;
          state.showStopTimerDialog = true;
        } else {
          state.timerStartError = payload?.message ?? 'Failed to start timer';
        }
      })
      // Handle stopTimerAsync
      .addCase(stopTimerAsync.fulfilled, (state, action) => {
        const stoppedTimer = state.activeTimers.find(t => t.taskId === action.payload.taskId);

        state.activeTimers = state.activeTimers.filter(t => t.taskId !== action.payload.taskId);
        state.activeTimer = state.activeTimers[0] || null;
        state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
        state.currentTimeLogId = undefined;

        // Add stopped task to the list (regardless of other running timers)
        if (stoppedTimer) {
          try {
            const stoppedTaskInfo = {
              taskId: stoppedTimer.taskId,
              taskTitle: stoppedTimer.taskTitle,
              stoppedAt: new Date().toISOString(),
            };
            
            // Get existing stopped tasks
            const stored = localStorage.getItem(STOPPED_TASKS_KEY);
            const existingStoppedTasks = stored ? JSON.parse(stored) : [];
            
            // Add new stopped task (remove if already exists to update timestamp)
            const updatedStoppedTasks = [
              stoppedTaskInfo,
              ...existingStoppedTasks.filter((t: { taskId: string }) => t.taskId !== stoppedTimer.taskId),
            ];
            
            localStorage.setItem(STOPPED_TASKS_KEY, JSON.stringify(updatedStoppedTasks));
            
            // Dispatch storage event for cross-tab synchronization
            window.dispatchEvent(new StorageEvent('storage', {
              key: STOPPED_TASKS_KEY,
              newValue: JSON.stringify(updatedStoppedTasks),
            }));
          } catch (error) {
            console.error('Failed to save stopped task:', error);
          }
        }
      })
      .addCase(stopTimerAsync.rejected, (state, action) => {
        const msg = (action.payload as Error & { message?: string })?.message ?? '';
        if (msg.includes('No active timer')) {
          state.activeTimers = [];
          state.activeTimer = null;
          state.isRunning = false;
          state.currentTimeLogId = undefined;
        } else {
          console.error('Failed to stop timer');
        }
      })
      // Pause: update state from backend response
      .addCase(pauseTimerAsync.fulfilled, (state, action) => {
        const payload = action.payload;
        const idx = state.activeTimers.findIndex(t => t.taskId === payload.taskId);
        if (idx !== -1) {
          state.activeTimers[idx] = { ...state.activeTimers[idx], ...payload };
        }
        state.activeTimer = state.activeTimers[0] || null;
        state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
      })
      .addCase(pauseTimerAsync.rejected, (state, action) => {
        const msg = (action.payload as Error & { message?: string })?.message ?? '';
        if (msg.includes('No active timer')) {
          state.activeTimers = [];
          state.activeTimer = null;
          state.isRunning = false;
          state.currentTimeLogId = undefined;
        } else {
          console.error('Failed to pause timer');
        }
      })
      // Resume: update state from backend response
      .addCase(resumeTimerAsync.fulfilled, (state, action) => {
        const payload = action.payload;
        const idx = state.activeTimers.findIndex(t => t.taskId === payload.taskId);
        if (idx !== -1) {
          state.activeTimers[idx] = { ...state.activeTimers[idx], ...payload };
        }
        state.activeTimer = state.activeTimers[0] || null;
        state.isRunning = state.activeTimers.some(t => t.status === TimerStatus.RUNNING);
      })
      .addCase(resumeTimerAsync.rejected, (state, action) => {
        const msg = (action.payload as Error & { message?: string })?.message ?? '';
        if (msg.includes('No active timer')) {
          state.activeTimers = [];
          state.activeTimer = null;
          state.isRunning = false;
          state.currentTimeLogId = undefined;
        } else {
          console.error('Failed to resume timer');
        }
      })
      // Handle pauseAllTimersAsync.fulfilled - Update all timers to paused state
      .addCase(pauseAllTimersAsync.fulfilled, (state, action) => {
        const pausedTimers = action.payload;
        // Update each timer in state with paused status from backend response
        // This ensures UI reflects the actual paused state from the database
        pausedTimers.forEach(pausedTimer => {
          const idx = state.activeTimers.findIndex(t => t.taskId === pausedTimer.taskId);
          if (idx !== -1) {
            state.activeTimers[idx] = { ...state.activeTimers[idx], ...pausedTimer };
          }
        });
        // Update legacy activeTimer reference for backward compatibility
        state.activeTimer = state.activeTimers[0] || null;
        // Set isRunning to false since all running timers are now paused
        state.isRunning = false;
      })
      // Handle pauseAllTimersAsync.rejected - Log error but don't clear timers
      .addCase(pauseAllTimersAsync.rejected, (state) => {
        // Log error but keep timers in state - user may want to retry
        console.error('Failed to pause all timers');
      });
  },
});

export const {
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  tick,
  setAccumulatedTime,
  restoreTimer,
  setPendingTimerTask,
  closeStopTimerDialog,
  clearTimerStartError,
  stopAndStartPending,
  clearTimerForTask,
} = timerSlice.actions;

// Async thunks are already exported at the top via createAsyncThunk

// Selectors
export const selectActiveTimer = (state: { timer: TimerState }) =>
  state.timer.activeTimer;
export const selectActiveTimers = (state: { timer: TimerState }) =>
  state.timer.activeTimers;
export const selectIsTimerRunning = (state: { timer: TimerState }) =>
  state.timer.isRunning;
export const selectTimerForTask =
  (taskId: string) => (state: { timer: TimerState }) =>
    state.timer.activeTimers.find(t => t.taskId === taskId) || null;
export const selectShowStopTimerDialog = (state: { timer: TimerState }) =>
  state.timer.showStopTimerDialog;
export const selectPendingTimerTask = (state: { timer: TimerState }) =>
  state.timer.pendingTimerTask;
export const selectTimerStartError = (state: { timer: TimerState }) =>
  state.timer.timerStartError;
export const selectTimerCount = (state: { timer: TimerState }) =>
  state.timer.activeTimers.length;

export default timerSlice.reducer;
